import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/browser';

import { ObjectType } from '$app-web/types';
import { isObject } from '$app-web/utils';

export function captureClientErrorsToSentry(axios: AxiosInstance): void {
    axios.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: AxiosError) => {
            const { response, config } = error;
            const { status, data } = response ?? {};

            if (response == null || status == null) {
                return error;
            }

            const errorName = `Error ${status} from "${config?.url}" endpoint`;
            const customError = new Error(errorName);

            customError.name = errorName;
            if (data != null && isObject(data) && 'error_extra_info' in (data as ObjectType)) {
                customError.message = (data as ObjectType).error_extra_info as string;
            }

            const request = {
                page_of_error: window.location.href,
                ...(config?.method?.toUpperCase() ? { method: config.method?.toUpperCase() } : {}),
                ...(config?.url ? { url: config.url } : {}),
                ...(config?.headers ? { request_headers: config.headers } : {}),
                ...(config?.data ? { request_data: config.data } : {}),
                ...(config?.params ? { request_params: config.params } : {}),
            };

            try {
                if (status === 400) {
                    Sentry.withScope(() => {
                        Sentry.captureException(customError, {
                            tags: { catch_in: 'Axios Interceptor', error_type: 'Bad Request (400)' },
                            extra: { request, response },
                        });
                    });
                }
            } catch (captureError) {
                // eslint-disable-next-line no-console
                console.error('Error capturing client error to Sentry:', captureError);
            }

            throw customError;
        }
    );
}
